import type { AuthError, SupabaseClient, User } from "@supabase/supabase-js";

/**
 * 一時的なネットワーク失敗で、リトライすれば復旧し得る認証エラーか判定する (#126)。
 *
 * AuthRetryableFetchError / "Failed to fetch" はオフライン・回線瞬断・Auth サーバーの
 * 一時無応答などで発生する。これらはユーザーを弾かず、呼び出し側でリトライさせる。
 */
export function isRetryableAuthError(
  error: Pick<AuthError, "name" | "message">,
): boolean {
  const name = error.name ?? "";
  const msg = error.message ?? "";
  return name === "AuthRetryableFetchError" || /failed to fetch/i.test(msg);
}

/**
 * getUser() を呼び、認証状態を解決する。
 *
 * - 正常 → User | null を返す
 * - 一時的なネットワーク失敗 (#126) → throw して呼び出し側のリトライに委ねる
 * - それ以外の認証エラー (セッション無し / refresh token 失効 #125) → 未ログイン扱い。
 *   stale な refresh token が残っていれば signOut で purge し、supabase-js の
 *   バックグラウンド自動リフレッシュが console.error を出し続ける暴発を止める。null を返す。
 *
 * getUser() は refresh token 失効時に throw せず error を返すため、原実装の
 * `.then(({data}) => data.user)` では stale トークンが purge されず #125 が再発していた。
 */
export async function getUserClearingStale(
  supabase: SupabaseClient,
): Promise<User | null> {
  const { data, error } = await supabase.auth.getUser();
  if (!error) return data.user;

  // 復旧可能なネットワークエラーは呼び出し側へ伝播（リトライさせる）
  if (isRetryableAuthError(error)) throw error;

  // AuthSessionMissingError は元々セッションが無いだけ（正常な未ログイン）。
  // purge 対象が無く、無駄な SIGNED_OUT 発火 (useSessionErrorHandler 連動) を避けるため何もしない。
  if (error.name !== "AuthSessionMissingError") {
    // refresh token 失効 (#125) 等: stale トークンを purge して背景リフレッシュの暴発を止める。
    await supabase.auth.signOut().catch(() => {});
  }
  return null;
}
